/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FieldArray, Form, Formik } from "formik";
import Colors from "constants/Colors";
import ListEnquiryButtons from 'components/ListEnquiryButtons';
import { GlobalAuthContext } from "store/context";
import { Layout, Text } from "components";
import Badge from 'components/Badge'
import Icon from "components/Icon";
import DialogComponent from 'components/Dialog';
import ContentCard from 'components/ContentCard/index';
import { FitinsurAPI, MessageCenter, CacheManager } from "utils";
import {
  TableHeader,
  TableCollum,
  Content,
  InlineLabel,
  LoadContainer,
  TableHead,
  TableRow,
  TableBody,
  Table,
  DialogIcon,
  ButtonContainer
} from "./styles";
import { Button } from "components/index";
import {
  LoaderContext
} from '@fitinsur/contexts';

import {
  GridColumnTitle,
  Label,
  ListHeader,
  ResponsiveTable
} from '@fitinsur/components';
import { useGetCreditAnalysisList } from '@fitinsur/services/OperationService';
import ModalInfo from "components/ModalInfo";
import { useGetQuery, useGetStatusOptions } from '@fitinsur/services/QuestionnairesService';

const initialFilterData = [
  { label: "NÚMERO", type: "text", key: "idOperation" },
  { label: "DATA DE CRIAÇÃO", type: "date", key: "operationDate" },
  { label: "USUÁRIO", type: "text", key: "userName" },
  { label: "CORRETORA", type: "list", items: [], key: "broker" },
  { label: "GRUPO ECONÔMICO", type: "text", key: "economicGroup" },
  { label: "TOMADOR", type: "text", key: "borrower" },
  { label: "CNPJ", type: "text", key: "borrowerLegalPersonId" },
  { label: "LIMITE APROVADO", type: "range", key: "approvedLimit" },
  { label: "VALIDO ATÉ", type: "date", key: "validUntil" },
  { label: "CCG GRUPO", type: "text", key: "ccgGroup" },
  { label: "SLA", type: "list", items: [], key: "sla" },
  { label: 'STATUS', type: 'list', items: [], key: 'idDomainStatus' },
];


export default function CreditAnalysisPage({ permissions }) {
  const authContext = useContext(GlobalAuthContext);
  const region = authContext.region === 'en-GB' ? 'en' : authContext.region;

  const cache = new CacheManager();
  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const history = useHistory();
  const location = useLocation();
  const formik = useRef();
  const [sortBy, setSortBy] = useState({
    key: null,
    direction: null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [personAdditionalInfoSelected, setPersonAdditionalInfoSelected] = useState([]);
  const [listData, setListData] = useState([]);
  const [message, setMessage] = useState([null]);
  const [brokerList, setBrokerList] = useState([]);
  const [optionsStatus, setOptionsStatus] = useState([]);
  const [dialog, setDialog] = useState({
    visible: false,
    title: '',
    icon: 'exclamation',
    args: null
  });

  const {
    data: getCreditAnalysisListData,
    loading: getCreditAnalysisListLoading,
    error: getCreditAnalysisListError,
    status: getCreditAnalysisListStatus,
    request: getCreditAnalysisListRequest,
  } = useGetCreditAnalysisList();

  const {
    request: useGetQueryRequest,
  } = useGetQuery();

  const {
    request: getStatusOptionsRequest,
  } = useGetStatusOptions();

  const initialValues = {
    filter: {
      number: "",
      firstName: "",
      fullName: "",
      hierarchy: "",
      parent: "",
      holding: "",
      personStatus: "",
    },
  };

  const columns = [
    {
      title: "NÚMERO",
      value: "idOperation",
      key: "idOperation",
      sortKey: "idOperation",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label fontSize={12}>
            <DialogIcon pink={"exclamation"}>
              {lisItem.warning ? "!" : ""}
            </DialogIcon>
            {lisItem.idOperation}
          </Label>
        )
      }
    },
    {
      title: "DATA DE CRIAÇÃO",
      value: "operationDate",
      key: "operationDate",
      sortKey: "operationDate",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label wordWrap fontSize={12}>
            {new Date(lisItem.operationDate).toLocaleDateString('pt-br')}
          </Label>
        )
      }
    },
    {
      title: "USUÁRIO",
      value: "userName",
      key: "userName",
      sortKey: "userName",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label wordWrap fontSize={12}>
            {lisItem.userName}
          </Label>
        )
      }
    },
    {
      title: "CORRETORA",
      value: "broker",
      key: "broker",
      sortKey: "broker",
      minWidth: 16,
      maxWidth: 16,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label wordWrap fontSize={12}>
            {lisItem.broker}
          </Label>
        )
      }
    },
    {
      title: "GRUPO ECONÔMICO",
      value: "economicGroup",
      key: "economicGroup",
      sortKey: "economicGroup",
      minWidth: 16,
      maxWidth: 16,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label wordWrap fontSize={12}>{lisItem.economicGroupName}</Label>
        )
      }
    },
    {
      title: "TOMADOR",
      value: "borrower",
      key: "borrower",
      sortKey: "borrower",
      minWidth: 16,
      maxWidth: 16,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label wordWrap fontSize={12}>
            {lisItem.borrower}
          </Label>
        )
      }
    },
    {
      title: "CNPJ",
      value: "borrowerLegalPersonId",
      key: "borrowerLegalPersonId",
      sortKey: "borrowerLegalPersonId",
      minWidth: 16,
      maxWidth: 16,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label wordWrap fontSize={12}>
            {lisItem.borrowerLegalPersonId}
          </Label>
        )
      }
    },
    {
      title: "LIMITE APROVADO",
      value: "approvedLimit",
      key: "approvedLimit",
      sortKey: "approvedLimit",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label wordWrap fontSize={12}>
            {lisItem.approvedLimit}
          </Label>
        )
      }
    },
    {
      title: "VALIDO ATÉ",
      value: "validUntil",
      key: "validUntil",
      sortKey: "validUntil",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label wordWrap fontSize={12}>
            {lisItem?.validUntil ? new Date(lisItem.validUntil).toLocaleDateString('pt-br', {timeZone: "UTC"}) : ""}
          </Label>
        )
      }
    },
    {
      title: "CCG GRUPO",
      value: "ccgGroup",
      key: "ccgGroup",
      sortKey: "ccgGroup",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label wordWrap fontSize={12}>{lisItem?.ccgGroup}</Label>
        )
      }
    },
    {
      title: "STATUS",
      value: "idDomainStatus",
      key: "idDomainStatus",
      sortKey: "idDomainStatus",
      minWidth: 15,
      maxWidth: 15,
      color: (item) => handleColorLabel(item),
      component: (listItem) => {
        return (
          <Label wordWrap fontSize={12}>
            {listItem?.operationMetadata?.integrationErrors?.length > 0 && listItem.operationMetadata.integrationErrors.some((error) => error.showInPopup) && (
              <Icon
                style={{ margin: 0, marginBottom: -3 }}
                name="info"
                className="mr-1"
                onClick={() => createModalIntegrationErrors(listItem.operationMetadata.integrationErrors)}
              />
            )}
            {[418, 419].includes(listItem.idDomainStatus) ?
              listItem?.idDomainStatusDescription :
              listItem?.document ? `${listItem?.document?.idDomainDescription} - ${listItem?.document?.idDomainStatusDescription}` :
                listItem?.idDomainStatusDescription}
          </Label>
        )
      }
    },
    {
      title: "SLA",
      value: "sla",
      key: "sla",
      sortKey: "sla",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item),
      component: (lisItem) => {
        return (
          <Label wordWrap fontSize={12}>
            {Badge({
              sla: {
                today: new Date(),
                operationDate: lisItem.operationDate,
                quotationExpirePeriod: lisItem.quotationExpirePeriod / (60 * 24),
                slaCompletionDate: lisItem.operationMetadata?.slaCompletionDate || null,
              }
            })}
          </Label>
        )
      }
    },
    {
      title: "AÇÕES",
      key: "action",
      minWidth: 8,
      maxWidth: 8,
      paddingRight: 5,
      component: (listItem) => <ListEnquiryButtons
        editRoute={checkOperationRoute}
        listItem={listItem}
        idOperation={listItem.idOperation}
        permissions={permissions || {}}
        authContextToken={authContext?.token}
        toDocument={() => history.push(`/broking/document-management/${listItem.idOperation}`)}
      />
    },
  ];

  function createModalIntegrationErrors(integrationErros) {
    const integrationErrors = integrationErros.filter((error) => error.service && error.userMessage && error.showInPopup);

    setDialog({
      visible: true,
      title: `Relatório de Integração`,
      icon: 'exclamation',
      args: integrationErrors,
    })
  }

  const storedFilterOption = localStorage.getItem('credit-analysis-filter-sompo');
  const storedStringFilterOption = localStorage.getItem('str-credit-analysis-filter-sompo');
  let quotesUrl = '/list' + (storedStringFilterOption ? '?' + storedStringFilterOption : '');

  const [filterData, setFilterData] = useState(storedFilterOption ? JSON.parse(storedFilterOption) : initialFilterData);

  const handleColorLabel = (item) => {
    if (item.isRejected) return Colors.GRAY[3];
    else if (item.isApproved) return Colors.GRAY[0];
    else if (item.idDomainStatusOper === 4 && item?.additionalInformation) return Colors.RED;
    else if (item.mediationFinished) return Colors.TERTIARY;
    else return null;
  }

  function handleSortList(key) {
    if (key === 'action' || key === 'status') return;
    let sort;
    if (sortBy.key === key && sortBy.direction === 'ASC') {
      sort = { key: key, direction: 'DESC' };
      setSortBy(sort);
    } else if (sortBy.key === key && sortBy.direction === 'DESC') {
      sort = { key: null, direction: null };
      setSortBy(sort);
    } else {
      sort = { key: key, direction: 'ASC' };
      setSortBy(sort);
    };
    getCreditAnalysisListRequest(null, {
      loadLastDocument: true,
      loadOperationPersonRoles: true,
      onlyRootOperations: true,
      ccgGroup: true,
      page: 1,
      idQuestionClientName: 827,
      idQuestionClientEmail: 828,
      idQuestionClientPhone: 829,
      idQuestionApplicationType: 1032,
      idsDomain: [686],
      idQuestionBorrowerLegalPersonId: 1634,
      idQuestionBorrower: 1635,
      idQuestionApprovedLimit: 1680,
      idQuestionValidUntil: 1686,
      sortBy: sort,
      ...(filterData.filter(v => v.value).reduce((a, b) => {
        a[b.key] = b.value;
        return a
      }, {})
      )
    });
  }

  const handleFilter = (resetFilter) => {
    quotesUrl = '/credit-analysis' + (localStorage.getItem('str-credit-analysis-filter-sompo') ? '?' + localStorage.getItem('str-credit-analysis-filter-sompo') : '')
    setCurrentPage(1);
    const refFilterData = resetFilter ? initialFilterData : filterData;
    getCreditAnalysisListRequest(null, {
      loadLastDocument: true,
      loadOperationPersonRoles: true,
      onlyRootOperations: true,
      ccgGroup: true,
      page: 1,
      idQuestionClientName: 827,
      idQuestionClientEmail: 828,
      idQuestionClientPhone: 829,
      idQuestionApplicationType: 1032,
      idsDomain: [686],
      idQuestionBorrowerLegalPersonId: 1634,
      idQuestionBorrower: 1635,
      idQuestionApprovedLimit: 1680,
      idQuestionValidUntil: 1686,
      sortBy,
      ...(refFilterData.filter(v => v.value).reduce((a, b) => {
        a[b.key] = b.value;
        return a
      }, {})
      )
    });

    if (resetFilter) {
      localStorage.removeItem('str-credit-analysis-filter-sompo')
      localStorage.removeItem('credit-analysis-filter-sompo')
      history.push(`/credit-analysis`)
    } else {
      history.push(`${quotesUrl}`)
    }
  };

  const exportList = async () => {
    const exportName = 'texel-export-list-report';
    let exportList = sessionStorage.getItem(exportName);
    let list = [];
    if (exportList) list = JSON.parse(sessionStorage.getItem(exportName))
    let verify = list.length > 0 ? list.filter(item => item.nameList == 'relatório de análises de crédito') : false;
    if (!verify || verify.length == 0) {
      updateIsLoadingStatus(true);
      setTimeout(() => updateIsLoadingStatus(false), 1000);

      let params = {
        loadLastDocument: true,
        loadOperationPersonRoles: true,
        onlyRootOperations: true,
        ccgGroup: true,
        idQuestionClientName: 827,
        idQuestionClientEmail: 828,
        idQuestionClientPhone: 829,
        idQuestionApplicationType: 1032,
        idQuestionBorrowerLegalPersonId: 1634,
        idQuestionBorrower: 1635,
        idQuestionApprovedLimit: 1680,
        idQuestionValidUntil: 1686,
        ...(filterData
          .filter(v => v.value)
          .reduce((a, b) => {
            a[b.key] = b.value;
            return a
          }, {}))
      };

      Object.keys(params).map((key) => {
        if (params[key] instanceof Date) {
          params[key] = params[key].toISOString();
        } else {
          if (Array.isArray(params[key])) {
            params[key] = params[key].map((value,index) => { 
              return (value instanceof Date ? value.toISOString() : value);
            });
          }
        } 
        return [];
      });

      let urlParams = new URLSearchParams();
      Object.keys(params).map((key) => {
        if (Array.isArray(params[key])) {
          params[key].forEach((value,index) => urlParams.append(key, value));
        } else {
          urlParams.append(key, params[key]);
        }        
        return [];
      });

      const url = `?${urlParams}&idsDomain[]=686`;

      fitinsur.setAuthToken(authContext.token);
      fitinsur.setAuthToken(authContext.token);
      const document = await fitinsur.exportCreditAnalysis(url).then(x => x.json());
      updateIsLoadingStatus(true);
      setTimeout(() => updateIsLoadingStatus(false), 1000);
      if (document.idDocument) {
        list.push({ idDocument: document.idDocument, processing: true, nameList: 'relatório de análises de crédito' });
        sessionStorage.setItem(exportName, JSON.stringify(list));
      }
    } else {
      mc.error("Por favor, aguarde o donwload do relatório de analises de crédito ser finalizado.");
    }
  };

  function setNextPage() {
    setCurrentPage(currentPage + 1)
    getCreditAnalysisListRequest(null, {
      loadLastDocument: true,
      loadOperationPersonRoles: true,
      onlyRootOperations: true,
      ccgGroup: true,
      page: currentPage + 1,
      idQuestionClientName: 827,
      idQuestionClientEmail: 828,
      idQuestionClientPhone: 829,
      idQuestionApplicationType: 1032,
      idsDomain: [686],
      idQuestionBorrowerLegalPersonId: 1634,
      idQuestionBorrower: 1635,
      idQuestionApprovedLimit: 1680,
      idQuestionValidUntil: 1686,
      sortBy,
      ...(filterData.filter(v => v.value).reduce((a, b) => {
        a[b.key] = b.value;
        return a
      }, {})
      )
    });
  }

  const RequestBrokerList = async () => {
    const { data } = await useGetQueryRequest({
      id: 'fitQueryPersonBroker',
      params: {
        id: "fitQueryPersonBroker",
        queryTail: true,
        personName: ''
      }
    });

    try {
      if (data.ret_code !== 200) return;

      const persons = data.ret_value.map(person => ({
        ...person,
        label: person.personName,
        value: person.id
      }));

      setBrokerList(persons);
    } catch (err) {
      console.log("err fitQueryPersonBroker", err)
    };
  };

  const requestStatusOptions = async () => {
    const { data } = await getStatusOptionsRequest();

    const idDomainsStatusMapped = [
      416, 417, 418, 419, 425, 439,
      420, 421, 422, 423, 424, 437,
      438, 440
    ];

    const statusCreditAnalysis = data.value.filter((f) =>
      idDomainsStatusMapped.includes(f.idDomainStatus)
    );

    const formattedStatus = statusCreditAnalysis.map(
      (status) => ({
        ...status,
        label: status.statusDescription,
        value: status.idDomainStatusList?.join(',') || status.idDomainStatus,
      })
    );

    setOptionsStatus(formattedStatus);
  };

  useEffect(() => {
    if (location.search) {
    } else {
      const queryString = localStorage.getItem('str-management-filter-meridian') || ""
      const filterQueryString = `?${queryString}`;
    }
  }, []);


  const checkOperationRoute = (idOperation, idProduct, idDomainStatusDoc) => {
    fitinsur.setAuthToken(authContext.token);
    updateIsLoadingStatus(true);

    fitinsur.requestPolicyNavigation(idOperation).then(x => x.json()).then(res => {
      if (res.ret_code !== 200) {
        mc.error(res.ret_msg_details);
        return;
      }
      let route = res.ret_value.route || `/credit-analysis/edit/57/${idOperation}`;
      if (route) {
        updateIsLoadingStatus(false);
        history.push({ pathname: route, state: res.ret_value.idNavigation == 99 ? { disabled: true, idProduct: idProduct } : { status: idDomainStatusDoc, idProduct: idProduct } })
      } else {
        mc.error('NAVIGATION NOT DEFINED');
        updateIsLoadingStatus(false);
      }
    }).catch(() => updateIsLoadingStatus(false));
  }


  useEffect(() => {
    getCreditAnalysisListRequest(null, {
      loadLastDocument: true,
      loadOperationPersonRoles: true,
      onlyRootOperations: true,
      ccgGroup: true,
      page: 1,
      idQuestionClientName: 827,
      idQuestionClientEmail: 828,
      idQuestionClientPhone: 829,
      idQuestionApplicationType: 1032,
      idsDomain: [686],
      idQuestionBorrowerLegalPersonId: 1634,
      idQuestionBorrower: 1635,
      idQuestionApprovedLimit: 1680,
      idQuestionValidUntil: 1686,
      sortBy,
      ...(filterData.filter(v => v.value).reduce((a, b) => {
        a[b.key] = b.value;
        return a
      }, {})
      )
    });

    fitinsur.setAuthToken(authContext.token);
    RequestBrokerList();
    requestStatusOptions();

    cache.getMessage("NO_ITEMS_LIST", region, "355", authContext.token).then(msg => setMessage(msg)).catch(err => console.log("error", err));
  }, []);

  useEffect(() => {
    if (getCreditAnalysisListStatus === 200) {
      if (getCreditAnalysisListData?.pagination?.page === 1) {
        setListData(getCreditAnalysisListData.value);
      } else {
        setListData([...listData, ...getCreditAnalysisListData.value]);
      }
      setTotalPages(+getCreditAnalysisListData?.pagination?.totalPages);
    }
  }, [getCreditAnalysisListStatus]);

  useEffect(() => {
    updateIsLoadingStatus(getCreditAnalysisListLoading)
  }, [getCreditAnalysisListLoading]);

  return (
    <Layout title="Análise de Crédito" subtitle="Nova análise de crédito:">
      {showNotesModal && <ModalInfo
        title={'Informações Adicionais'}
        setModalShow={setShowNotesModal}
        note={personAdditionalInfoSelected}
        disabled
      />
      }

      <div style={{ display: "flex", width: "100%", gap: "20px" }}>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100px',
            cursor: "pointer",
            border: '1px solid #EEEEEE',
            borderRadius: 4,
            transition: 'transform 0.2s',
            transform: 'scale(1)',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.03)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
          onClick={() => history.push(`/credit-analysis/create/57`)}
        >
          <Icon name="policyholder" />
        </div>
      </div>
      <Content>

        <DialogComponent icon={dialog.icon} size='60%' show={dialog.visible} setDialog={setDialog}>
          <ContentCard title={dialog.title} noBorder={true}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ width: "100%" }}>
                {dialog.args?.map(arg => {
                  return (
                    <p><span style={{ fontWeight: "bold" }}>{arg.service}:&nbsp;</span>{arg.userMessage}</p>
                  )
                })}
              </div>

              <Button
                third
                style={{ ju: '100%', marginTop: '20px' }}
                onClick={() => setDialog({ visible: false, title: null, icon: null, args: null })}
              >
                Fechar
              </Button>
            </div>
          </ContentCard>
        </DialogComponent>

        <Formik
          initialValues={initialValues}
          onSubmit={(values) => handleFilter(values)}
          innerRef={formik}
        >
          {(formikProps) => (
            <Form>
              <ListHeader
                idDomainList={716}
                idApplication={675}
                filterData={filterData}
                setFilterData={setFilterData}
                onHandleFilter={(resetFilter) => handleFilter(resetFilter)}
                listNameSpace={'credit-analysis-filter-sompo'}
                optionsBroker={brokerList}
                optionsSla={[
                  { label: 'Cinza', value: 'gray' },
                  { label: 'Verde', value: 'green' },
                  { label: 'Amarelo', value: 'yellow' },
                  { label: 'Vermelho', value: 'red' }
                ]}
                optionsStatus={optionsStatus}
                listUrl='/credit-analysis'
                exportList={() => exportList(location.search || undefined)}
                availabelStatuses={[
                  416, 417, 418, 419, 425, 439,
                  420, 421, 422, 423, 424, 437,
                  438, 440
                ]}
              />

              <ResponsiveTable onSortTable={handleSortList} formikRef={formik?.current} columns={columns} data={listData} />
              {!listData.length && (
                <ButtonContainer style={{ minHeight: '450px' }}>
                  <Text style={{ fontWeight: 'bold', color: Colors.GRAY[1], margin: 'auto', cursor: 'pointer', marginTop: '10px' }} onClick={() => { }}>{message || ''}</Text>
                </ButtonContainer>
              )
              }

            </Form>
          )}
        </Formik>
        <LoadContainer>
          {!listData || listData.length === 0 || currentPage === totalPages ? (
            <Button forth onClick={(e) => e.preventDefault()}>
              NÃO HÁ MAIS ITENS
            </Button>
          ) : (
            <Button forth onClick={() => setNextPage()}>
              + CARREGAR MAIS
            </Button>
          )}
        </LoadContainer>
      </Content>
    </Layout>
  );
}